import type { FC } from 'react';
import React, { useRef } from 'react';
import cn from 'classnames';
import { useFocus } from '@alfalab/hooks';

import type { TCheckbox } from './models';
import { renderCheckMarkSVG } from './render-check-mark';
import { getAddonTheme, getCaptionTheme } from './helpers/get-themes';

export const Checkbox: FC<TCheckbox> = ({
    Text,
    addons = '',
    align,
    block = false,
    caption = '',
    checked = false,
    children,
    className = '',
    color = 'red',
    dataTestId,
    disabled = false,
    error = false,
    errorText = '',
    forwardedRef,
    name = 'default_checkbox',
    onChange,
    size = 'm',
    styles,
    theme = 'white',
    type = 'checkmark',
    value = '',
}) => {
    const labelRef = useRef<HTMLLabelElement>(null);

    const [focused] = useFocus(labelRef, 'keyboard');

    const checkboxStyles = cn(
        styles.checkboxContainer,
        styles[`${theme}Theme`],
        styles[`${size}Size`],
        styles[`${type}Type`],
        {
            // Не задаем align по умолчанию, т.к. в некоторых командных виджетах едет верстка
            [styles.startAlign]: align === 'start',
            [styles.centerAlign]: align === 'center',
            [styles.greenColor]: color === 'green',
            [styles.checked]: checked,
            [styles.disabled]: disabled,
            [styles.checkboxError]: error,
            [styles.block]: block,
        }
    );

    return (
        <div className={className} data-test-id={dataTestId}>
            <label ref={labelRef} className={checkboxStyles}>
                <input
                    ref={forwardedRef}
                    checked={checked}
                    className={styles.input}
                    data-test-id={`${name}-checkbox`}
                    disabled={disabled}
                    name={name}
                    onChange={event => {
                        onChange({ checked: !checked, name, value, event });
                    }}
                    // при клике на чекбокс происходит 2 вызова евента из-за обертки в label, остановка всплытия решает эту проблему
                    onClick={event => event.stopPropagation()}
                    type="checkbox"
                    value={value}
                />
                <div
                    className={cn(styles.checkbox, {
                        [styles.focused]: focused,
                    })}
                    data-test-id={`${name}-checkbox-area`}
                    data-test-selected={`${checked}`}
                >
                    {checked && renderCheckMarkSVG(type, size)}
                </div>
                <div>
                    {children && <div data-test-id={`${name}-checkbox-caption`}>{children}</div>}
                    {(caption || errorText) && (
                        <Text
                            className={cn(styles.caption, styles.external)}
                            kind={getCaptionTheme(error, errorText, theme, disabled)}
                            view="component-secondary"
                        >
                            {errorText || caption}
                        </Text>
                    )}
                </div>
                {addons && (
                    <Text
                        className={cn(styles.addons, styles.external)}
                        kind={getAddonTheme(theme, disabled)}
                        styler={{ paddingLeft: 8 }}
                        tag="span"
                        view="primary-medium"
                    >
                        {addons}
                    </Text>
                )}
            </label>
        </div>
    );
};
